/**
 * FeedbackPage — combined "submit + history" landing page for end users.
 * Lives under /dashboard/feedback. The submit form sits on top, followed
 * by a filterable history list. Detail views live at /dashboard/feedback/:id.
 */
import React, { Fragment, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { DashboardHeader } from '@/src/modules/components/Header';
import {
  listMyFeedback,
  type ApiFeedback,
  type FeedbackStatus,
  type FeedbackType,
} from '@/src/infrastructure/lib/api';
import { cn } from '@/src/infrastructure/lib/utils';
import { FeedbackForm } from './FeedbackForm';
import { FeedbackCard } from './FeedbackCard';
import { FEEDBACK_TYPE_OPTIONS, STATUS_LABEL } from './constants';

const STATUS_FILTERS: { value: 'ALL' | FeedbackStatus; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'OPEN', label: STATUS_LABEL.OPEN },
  { value: 'IN_REVIEW', label: STATUS_LABEL.IN_REVIEW },
  { value: 'RESOLVED', label: STATUS_LABEL.RESOLVED },
  { value: 'CLOSED', label: STATUS_LABEL.CLOSED },
];

export function FeedbackPage() {
  const [items, setItems] = useState<ApiFeedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | FeedbackStatus>('ALL');
  const [typeFilter, setTypeFilter] = useState<'ALL' | FeedbackType>('ALL');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    listMyFeedback({
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      type: typeFilter === 'ALL' ? undefined : typeFilter,
      limit: 50,
    })
      .then((page) => {
        if (!cancelled) setItems(page.items);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load feedback');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter, typeFilter]);

  return (
    <div className="px-4 md:px-8 py-6 md:py-10 space-y-8 max-w-4xl mx-auto">
      <DashboardHeader
        title="Feedback"
        description="Tell us what's working — and what isn't."
      />

      <FeedbackForm
        onCreated={(fb) => setItems((prev) => [fb, ...prev])}
      />

      <motion.section
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="space-y-4"
      >
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-base font-bold text-on-surface">Your submissions</h2>
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setStatusFilter(f.value)}
                aria-pressed={statusFilter === f.value}
                className={cn(
                  'rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
                  statusFilter === f.value
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-outline-variant/40 text-on-surface-variant hover:text-on-surface',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </header>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setTypeFilter('ALL')}
            aria-pressed={typeFilter === 'ALL'}
            className={cn(
              'rounded-full border px-3 py-1 text-xs transition-colors',
              typeFilter === 'ALL'
                ? 'border-on-surface bg-on-surface/10 text-on-surface'
                : 'border-outline-variant/40 text-on-surface-variant hover:text-on-surface',
            )}
          >
            All types
          </button>
          {FEEDBACK_TYPE_OPTIONS.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTypeFilter(t.value)}
              aria-pressed={typeFilter === t.value}
              className={cn(
                'rounded-full border px-3 py-1 text-xs transition-colors',
                typeFilter === t.value
                  ? 'border-on-surface bg-on-surface/10 text-on-surface'
                  : 'border-outline-variant/40 text-on-surface-variant hover:text-on-surface',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-24 rounded-2xl border border-outline-variant/30 bg-surface-lowest animate-pulse"
              />
            ))}
          </div>
        )}

        {!loading && error && (
          <div role="alert" className="rounded-xl border border-rose-500/30 bg-rose-50/60 p-4 text-sm text-rose-700">
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <EmptyState />
        )}

        {!loading && !error && items.length > 0 && (
          <div className="space-y-3">
            {items.map((fb) => (
              <Fragment key={fb.id}>
                <FeedbackCard feedback={fb} />
              </Fragment>
            ))}
          </div>
        )}
      </motion.section>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-outline-variant/50 px-6 py-12 text-center">
      <p className="text-sm text-on-surface-variant">
        You haven't submitted any feedback yet.
      </p>
      <p className="text-xs text-on-surface-variant/70 mt-1">
        Use the form above to send us a bug report, feature idea, or anything else.
      </p>
    </div>
  );
}
